import { useMemo } from 'react';

import { Permission } from '@bookora/shared';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { clinicProceduresList } from '@/api/generated/clinic-procedures/clinic-procedures';
import {
  consentsRequirementStatus,
  consentsSessionStatus,
} from '@/api/generated/consents/consents';
import {
  injectionMapsCreate,
  injectionMapsLast,
  injectionMapsList,
  injectionMapsListTemplates,
  injectionMapsUpdate,
} from '@/api/generated/injection-maps/injection-maps';
import { inventorySuggest } from '@/api/generated/inventory/inventory';
import { inventoryLotsList } from '@/api/generated/inventory-—-lots/inventory-—-lots';
import { materialsList } from '@/api/generated/inventory-—-materials/inventory-—-materials';
import { patientPaymentsCreate } from '@/api/generated/accounting-patient-payments/accounting-patient-payments';
import { patientPackagesList } from '@/api/generated/patient-packages/patient-packages';
import type {
  AppointmentResponse,
  ClinicProcedureResponse,
  ConsentRequirementStatusResponse,
  ConsumptionSuggestionResponse,
  CreateInjectionMapDto,
  CreatePatientPaymentDto,
  CreateSessionDto,
  InjectionMapDraftResponse,
  InjectionMapResponse,
  InjectionMapTemplateResponse,
  LotResponse,
  MaterialResponse,
  PatientPackageResponse,
  PatientResponse,
  PatientSummaryResponse,
  RecordedSessionResponse,
  RoomWithBranchResponse as RoomResponse,
  SessionConsentStatusResponse,
  SessionListResponse,
  SessionResponse,
  SessionsListParams,
  StaffResponse,
  SurveySendResponse,
  UpdateInjectionMapDto,
} from '@/api/generated/model';
import { patientsFindOne, patientsList } from '@/api/generated/patients/patients';
import { remindersCreate } from '@/api/generated/reminders/reminders';
import { roomsList } from '@/api/generated/rooms/rooms';
import { surveysSend } from '@/api/generated/satisfaction-surveys/satisfaction-surveys';
import {
  appointmentsFindOne,
  appointmentsList,
} from '@/api/generated/scheduling-—-appointments/scheduling-—-appointments';
import { staffList } from '@/api/generated/staff/staff';
import {
  sessionsAddConsumptions,
  sessionsCancel,
  sessionsComplete,
  sessionsConsumptionSuggestions,
  sessionsGetById,
  sessionsList,
  sessionsRecord,
  sessionsRemove,
  sessionsRemoveConsumption,
  sessionsUpdate,
} from '@/api/generated/treatment-sessions/treatment-sessions';
import { branchKey } from '@/api/query-keys';
import { useAuth } from '@/auth/token-store';
import { branchParam } from '@/features/branches/contract';
import { useBranchContext } from '@/features/branches/hooks';
import { packageKeys } from '@/features/packages/api';
import { patientKeys } from '@/features/patients/keys';
import { useCan } from '@/permissions/use-can';

import { OPEN_APPOINTMENT_STATUSES, nearbyWindow } from './appointment-match';
import { baselineKey, type ConsumptionLine, type OverrideBaselines } from './consumption-lines';
import { eligiblePackages } from './package-eligibility';
import { sessionKeys } from './query-keys';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE SESSIONS SECTION'S READS AND WRITES
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Every function here wraps a GENERATED orval client — nothing in this section
 * builds a URL or calls axios by hand. The generated module is the contract:
 * when the OpenAPI document changes, `pnpm gen:api` moves the types under these
 * hooks and the compiler points at every screen that has to follow.
 *
 * What is added on top of the generated layer is only what orval cannot know:
 *
 *  · **Clinic-scoped query keys.** See `query-keys.ts`.
 *  · **Permission gating on `enabled`.** A screen never fires a request the
 *    caller cannot make. That is not security — the server guards each route
 *    independently — it is the difference between a nurse seeing a section
 *    without money in it and a nurse seeing a console full of red 403s.
 *  · **Invalidation.** A recorded session moves stock, so the lot lists have to
 *    be dropped alongside the session list; a completed session changes the
 *    consent state that gated the button that completed it.
 *
 * The generated hooks themselves are deliberately NOT used: this spec's orval
 * configuration emits `useMutation` for GET operations and `useQuery` for
 * POST/PATCH/DELETE, which is inverted. The generated *functions* are correct
 * and are what these hooks call.
 */

/** A generated request's params with the branch added (a variable, so no excess-property check). */
function withBranch<T extends object>(params: T, branchId: string | null | undefined): T {
  return { ...params, ...branchParam(branchId) };
}

/* -------------------------------------------------------------------------- */
/* Reads                                                                      */
/* -------------------------------------------------------------------------- */

export function useSessionsList(
  params: SessionsListParams,
  enabled = true,
): UseQueryResult<SessionListResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SESSION_VIEW);
  // The switcher's branch (multi-branch); «همهٔ شعبه‌ها» sends none.
  const branch = useBranchContext();
  const query: SessionsListParams = { ...params, ...branchParam(branch.scope) };

  return useQuery({
    queryKey: sessionKeys.list(activeClinicId, params as Record<string, unknown>, branch.key),
    queryFn: ({ signal }) => sessionsList(query, signal),
    enabled: enabled && canView && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

export function useSession(sessionId: string | null): UseQueryResult<SessionResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SESSION_VIEW);

  return useQuery({
    queryKey: sessionKeys.detail(activeClinicId, sessionId ?? ''),
    queryFn: ({ signal }) => sessionsGetById(sessionId as string, signal),
    enabled: Boolean(sessionId) && canView && Boolean(activeClinicId),
    staleTime: 15_000,
  });
}

/**
 * Consent state for one session.
 *
 * Gated on `consent.view` — a receptionist who may record a session but may not
 * read consent state must not be told, via a 403 in the console, that the
 * question was even asked. When the query is disabled the finalise dialog says
 * so plainly instead of pretending the consent is fine.
 */
export function useSessionConsentStatus(
  sessionId: string | null,
  options: { poll?: boolean } = {},
): UseQueryResult<SessionConsentStatusResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.CONSENT_VIEW);

  return useQuery({
    queryKey: sessionKeys.consentStatus(activeClinicId, sessionId ?? ''),
    queryFn: ({ signal }) => consentsSessionStatus({ sessionId: sessionId as string }, signal),
    enabled: Boolean(sessionId) && canView && Boolean(activeClinicId),
    staleTime: 10_000,
    // See `CONSENT_POLL_MS`: only while a sign link is out, and only until the
    // answer is the one being waited for.
    refetchInterval: (query) =>
      options.poll && query.state.data?.satisfied !== true ? CONSENT_POLL_MS : false,
  });
}

/**
 * How often an open screen re-asks while a sign link is with the patient.
 *
 * The patient signs on their own phone, so nothing in this tab learns about it
 * by itself. Five seconds is quick enough that the desk sees the card turn
 * green while the patient is still holding the phone, and the poll stops the
 * moment the answer is «satisfied» or the screen closes.
 */
export const CONSENT_POLL_MS = 5_000;

/**
 * Consent state for a session that does not exist yet — «ثبت جلسه» asks it as
 * soon as a patient and a service are both chosen.
 *
 * `satisfied` is, by the server's own construction, exactly whether
 * `POST /sessions` with `COMPLETED` would pass the consent gate — the endpoint
 * and the write share one method in `ConsentRequirementService`. So the form
 * can disable «ثبت جلسه» on it without guessing, and a 422 after a green card
 * can only be a race (somebody voided the consent in between).
 */
export function useConsentRequirement(
  subject: {
    patientId: string | null;
    procedureId: string | null;
    clinicProcedureId: string | null;
  },
  options: { enabled: boolean; poll?: boolean },
): UseQueryResult<ConsentRequirementStatusResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.CONSENT_VIEW);
  const { patientId, procedureId, clinicProcedureId } = subject;

  return useQuery({
    queryKey: sessionKeys.consentRequirement(
      activeClinicId,
      patientId ?? '',
      procedureId ?? '',
      clinicProcedureId ?? '',
    ),
    queryFn: ({ signal }) =>
      consentsRequirementStatus(
        {
          patientId: patientId as string,
          ...(procedureId ? { procedureId } : {}),
          ...(clinicProcedureId ? { clinicProcedureId } : {}),
        },
        signal,
      ),
    enabled:
      options.enabled &&
      canView &&
      Boolean(activeClinicId) &&
      Boolean(patientId) &&
      Boolean(procedureId || clinicProcedureId),
    staleTime: 10_000,
    refetchInterval: (query) =>
      options.poll && query.state.data?.satisfied !== true ? CONSENT_POLL_MS : false,
  });
}

/**
 * The server's FEFO suggestion for a procedure's default consumables.
 *
 * This is the SEED for the consumption form. Once a line is edited the panel
 * re-derives the suggestion from the lot list — see `fefo.ts` for why both
 * exist and why they must not disagree silently.
 */
export function useConsumptionSuggestions(
  params: { procedureId?: string; clinicProcedureId?: string },
  enabled: boolean,
): UseQueryResult<ConsumptionSuggestionResponse> {
  const { activeClinicId } = useAuth();
  const canInventory = useCan(Permission.INVENTORY_VIEW);
  // The route asks for both keys. The finalise dialog is reached with
  // `session.edit` alone, and a 403 there would read as «no default materials».
  const canCreate = useCan(Permission.SESSION_CREATE);

  return useQuery({
    queryKey: sessionKeys.consumptionSuggestions(activeClinicId, params),
    queryFn: ({ signal }) => sessionsConsumptionSuggestions(params, signal),
    enabled:
      enabled &&
      canInventory &&
      canCreate &&
      Boolean(activeClinicId) &&
      Boolean(params.procedureId),
    staleTime: 30_000,
  });
}

/**
 * The server's override baseline for every (material, quantity) on the form.
 *
 * `GET /inventory/consumption/suggest` runs the same allocator the write does
 * and returns `defaultFirstLotId` — the lot it would draw from first if the
 * caller named none. That, not the panel's local FEFO copy, is what decides
 * whether a written reason is owed (see `OverrideBaselines`). One query per
 * distinct (material, quantity), so retyping a quantity re-asks and a second
 * line of the same material at the same quantity costs nothing.
 *
 * Read defensively: a server without the field yields no entry, and the local
 * rule applies until the contract catches up.
 */
export function useOverrideBaselines(
  lines: readonly Pick<ConsumptionLine, 'materialId' | 'quantityMilli'>[],
  options: { branchId?: string | null; enabled?: boolean } = {},
): OverrideBaselines {
  const { activeClinicId } = useAuth();
  const canConsume = useCan(Permission.INVENTORY_CONSUME) && options.enabled !== false;
  const branchId = options.branchId ?? undefined;

  const asks = useMemo(() => {
    const seen = new Map<string, { materialId: string; quantityMilli: number }>();
    for (const line of lines) {
      if (line.quantityMilli === null || line.quantityMilli <= 0) continue;
      const key = baselineKey(line.materialId, line.quantityMilli);
      if (!seen.has(key)) {
        seen.set(key, { materialId: line.materialId, quantityMilli: line.quantityMilli });
      }
    }
    return Array.from(seen.entries());
  }, [lines]);

  const results = useQueries({
    queries: asks.map(([, ask]) => ({
      queryKey: sessionKeys.overrideBaseline(
        activeClinicId,
        ask.materialId,
        ask.quantityMilli,
        branchKey(branchId),
      ),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        inventorySuggest(withBranch(ask, branchId), signal),
      enabled: canConsume && Boolean(activeClinicId),
      staleTime: 15_000,
    })),
  });

  const out: Record<string, string | null> = {};
  asks.forEach(([key], index) => {
    const plan = results[index]?.data;
    if (!plan) return;
    const raw = (plan as { defaultFirstLotId?: unknown }).defaultFirstLotId;
    if (raw === undefined) return;
    out[key] = typeof raw === 'string' ? raw : null;
  });
  return out;
}

/**
 * Every lot of one material that still holds stock.
 *
 * `inStockOnly` rather than `status=ACTIVE`: a quarantined or expired lot is
 * still worth showing in the override list — greyed out and unselectable — so
 * the operator can see the batch they were looking for and understand why they
 * may not draw from it. `isConsumable` on each row carries that answer.
 */
export function useLotsForMaterial(
  materialId: string | null,
  branchId?: string | null,
): UseQueryResult<LotResponse[]> {
  const { activeClinicId } = useAuth();
  const canInventory = useCan(Permission.INVENTORY_VIEW);

  return useQuery({
    queryKey: sessionKeys.lotsForMaterial(activeClinicId, materialId ?? '', branchKey(branchId)),
    queryFn: async ({ signal }) => {
      const page = await inventoryLotsList(
        withBranch(
          { materialId: materialId as string, inStockOnly: true, pageSize: 100 },
          branchId ?? undefined,
        ),
        signal,
      );
      return page.items;
    },
    enabled: Boolean(materialId) && canInventory && Boolean(activeClinicId),
    staleTime: 15_000,
  });
}

/**
 * The same lot lists, for every material on the consumption form at once.
 *
 * `useQueries` rather than a `useLotsForMaterial` per row, because the number
 * of rows changes as the clinician adds materials and hooks cannot be called in
 * a loop whose length varies. The per-material queries share their cache keys
 * with `useLotsForMaterial`, so a row added twice costs one request.
 */
export function useLotsForMaterials(
  materialIds: readonly string[],
  options: { branchId?: string | null; enabled?: boolean } = {},
): {
  byMaterial: Record<string, LotResponse[]>;
  isLoading: boolean;
} {
  const { activeClinicId } = useAuth();
  const canInventory = useCan(Permission.INVENTORY_VIEW) && options.enabled !== false;
  const branchId = options.branchId ?? undefined;

  const results = useQueries({
    queries: materialIds.map((materialId) => ({
      queryKey: sessionKeys.lotsForMaterial(activeClinicId, materialId, branchKey(branchId)),
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const page = await inventoryLotsList(
          withBranch({ materialId, inStockOnly: true, pageSize: 100 }, branchId),
          signal,
        );
        return page.items;
      },
      enabled: canInventory && Boolean(activeClinicId),
      staleTime: 15_000,
    })),
  });

  const byMaterial: Record<string, LotResponse[]> = {};
  materialIds.forEach((materialId, index) => {
    byMaterial[materialId] = results[index]?.data ?? [];
  });

  return { byMaterial, isLoading: results.some((result) => result.isLoading) };
}

export function useMaterials(enabled: boolean): UseQueryResult<MaterialResponse[]> {
  const { activeClinicId } = useAuth();
  const canInventory = useCan(Permission.INVENTORY_VIEW);

  return useQuery({
    queryKey: [...sessionKeys.root(activeClinicId), 'materials'] as const,
    queryFn: async ({ signal }) => {
      const page = await materialsList({ isActive: true, pageSize: 100 }, signal);
      return page.items;
    },
    enabled: enabled && canInventory && Boolean(activeClinicId),
    staleTime: 5 * 60_000,
  });
}

export function useClinicProcedures(): UseQueryResult<ClinicProcedureResponse[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PROCEDURE_VIEW);

  return useQuery({
    queryKey: sessionKeys.procedures(activeClinicId),
    queryFn: async ({ signal }) => {
      const page = await clinicProceduresList({ isEnabled: true, pageSize: 100 }, signal);
      return page.items;
    },
    enabled: canView && Boolean(activeClinicId),
    staleTime: 5 * 60_000,
  });
}

export function useDoctors(): UseQueryResult<StaffResponse[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.STAFF_VIEW);

  return useQuery({
    queryKey: sessionKeys.doctors(activeClinicId),
    queryFn: async ({ signal }) => {
      const page = await staffList({ isDoctor: true, isActive: true, pageSize: 100 }, signal);
      return page.items;
    },
    enabled: canView && Boolean(activeClinicId),
    staleTime: 5 * 60_000,
  });
}

export function useRooms(): UseQueryResult<RoomResponse[]> {
  const { activeClinicId } = useAuth();
  // `GET /rooms` is `RequireAnyPermission(settings.view, appointment.view)`;
  // this was the one session read with no gate, so a role holding neither
  // got a console 403 on opening «ثبت جلسه».
  const canSettings = useCan(Permission.SETTINGS_VIEW);
  const canAppointments = useCan(Permission.APPOINTMENT_VIEW);

  return useQuery({
    queryKey: sessionKeys.rooms(activeClinicId),
    queryFn: ({ signal }) => roomsList({ isActive: true }, signal),
    enabled: Boolean(activeClinicId) && (canSettings || canAppointments),
    staleTime: 5 * 60_000,
  });
}

export function usePatient(patientId: string | null): UseQueryResult<PatientResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PATIENT_VIEW);

  return useQuery({
    queryKey: sessionKeys.patient(activeClinicId, patientId ?? ''),
    queryFn: ({ signal }) => patientsFindOne(patientId as string, signal),
    enabled: Boolean(patientId) && canView && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

/**
 * The appointment a «ثبت جلسه» deep link names (`?new=1&appointmentId=`).
 *
 * Read for what it already knows — patient, service, doctor, room, time — and
 * for `sessionId`, which says a session was already recorded against it. Gated
 * on `appointment.view`: without it the form still sends the link and the
 * server validates it; it just cannot pre-fill from it.
 */
export function useAppointmentSeed(
  appointmentId: string | null,
): UseQueryResult<AppointmentResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.APPOINTMENT_VIEW);

  return useQuery({
    queryKey: [...sessionKeys.root(activeClinicId), 'appointment', appointmentId ?? ''] as const,
    queryFn: ({ signal }) => appointmentsFindOne(appointmentId as string, signal),
    enabled: Boolean(appointmentId) && canView && Boolean(activeClinicId),
    staleTime: 15_000,
    // A deleted or foreign appointment is a 404 the form explains by simply
    // not pre-filling; three retries would only delay that.
    retry: false,
  });
}

/**
 * The patient's open appointments from yesterday to tomorrow — the pool
 * `matchNearbyAppointment` picks «وصل به نوبت ساعت …» from. Only asked when
 * the form has no appointment of its own; without `appointment.view` nothing
 * is asked and the form simply offers no link.
 */
export function useNearbyAppointments(
  patientId: string | null,
  enabled: boolean,
): UseQueryResult<AppointmentResponse[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.APPOINTMENT_VIEW);

  return useQuery({
    queryKey: [
      ...sessionKeys.root(activeClinicId),
      'nearby-appointments',
      patientId ?? '',
    ] as const,
    queryFn: async ({ signal }) => {
      const page = await appointmentsList(
        {
          patientId: patientId as string,
          ...nearbyWindow(new Date()),
          status: [...OPEN_APPOINTMENT_STATUSES],
          pageSize: 20,
        },
        signal,
      );
      return page.items;
    },
    enabled: enabled && Boolean(patientId) && canView && Boolean(activeClinicId),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Patient lookup for the "who was this session for" field.
 *
 * This used to carry a shape-narrowing workaround: `GET /patients` declared the
 * `{ data }` envelope in its OpenAPI schema while the transport had already
 * stripped it, so the generated type sat one level deeper than the value that
 * actually arrived. That was fixed at the source — the declared type and the
 * wire now agree — so the narrowing is gone rather than adapted.
 */
export function usePatientSearch(
  term: string,
  enabled: boolean,
): UseQueryResult<PatientSummaryResponse[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PATIENT_VIEW);

  return useQuery({
    queryKey: [...sessionKeys.root(activeClinicId), 'patient-search', term] as const,
    queryFn: async ({ signal }) => {
      const response = await patientsList(
        { ...(term.trim() ? { q: term.trim() } : {}), pageSize: 20 },
        signal,
      );
      return response.items;
    },
    enabled: enabled && canView && Boolean(activeClinicId),
    staleTime: 30_000,
  });
}

export function useSessionInjectionMaps(
  sessionId: string | null,
): UseQueryResult<InjectionMapResponse[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SESSION_VIEW);

  return useQuery({
    queryKey: sessionKeys.injectionMap(activeClinicId, sessionId ?? ''),
    queryFn: ({ signal }) => injectionMapsList({ sessionId: sessionId as string }, signal),
    enabled: Boolean(sessionId) && canView && Boolean(activeClinicId),
    staleTime: 15_000,
  });
}

export function useInjectionTemplates(
  procedureId: string | null,
  enabled: boolean,
): UseQueryResult<InjectionMapTemplateResponse[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SESSION_VIEW);

  return useQuery({
    queryKey: sessionKeys.injectionTemplates(activeClinicId, procedureId),
    queryFn: ({ signal }) =>
      injectionMapsListTemplates(
        { ...(procedureId ? { procedureId } : {}), activeOnly: true },
        signal,
      ),
    enabled: enabled && canView && Boolean(activeClinicId),
    staleTime: 5 * 60_000,
  });
}

/**
 * The patient's last map for this procedure, as a DRAFT.
 *
 * `GET /injection-maps/last` writes nothing — it returns the previous map's
 * points so the editor can be seeded with them. Persisting is a separate,
 * explicit save, which is what keeps "the doctor looked at last time's map"
 * from becoming "the doctor recorded last time's map again".
 */
export function useLastInjectionMap(
  patientId: string | null,
  procedureId: string | null,
  enabled: boolean,
): UseQueryResult<InjectionMapDraftResponse | null> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SESSION_VIEW);

  return useQuery({
    queryKey: [...sessionKeys.root(activeClinicId), 'last-map', patientId, procedureId] as const,
    queryFn: async ({ signal }) => {
      // A first visit has no previous map: the route answers `null`
      // (200, not 404) and that is a state the editor explains, not an error.
      const draft = await injectionMapsLast(
        { patientId: patientId as string, ...(procedureId ? { procedureId } : {}) },
        signal,
      );
      return draft ?? null;
    },
    enabled: enabled && Boolean(patientId) && canView && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

/**
 * The chosen patient's packages that can pay for a session of this service —
 * the options of the package card (`package-choice.tsx`), best first.
 *
 * `package.view` gates the list endpoint, so without it nothing is asked and
 * the card never appears: the session is simply paid separately, which is
 * what the form did before the card existed. No `keepPreviousData` here, on
 * purpose — the list must never show the previous patient's package while the
 * next patient's loads, and the rows are checked against the patient as well.
 */
export function useSessionPackages(
  patientId: string | null,
  procedureId: string | null,
  enabled: boolean,
): {
  packages: PatientPackageResponse[];
  isLoading: boolean;
  isKnown: boolean;
  isError: boolean;
} {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.PACKAGE_VIEW);
  const params = { patientId: patientId ?? '', pageSize: 100 };
  const active =
    enabled && canView && Boolean(activeClinicId) && Boolean(patientId) && Boolean(procedureId);
  const query = useQuery({
    queryKey: packageKeys.sales(activeClinicId, params),
    queryFn: ({ signal }) => patientPackagesList(params, signal),
    enabled: active,
    staleTime: 30_000,
  });
  const packages = useMemo(
    () =>
      active
        ? eligiblePackages(
            query.data?.items.filter((sale) => sale.patientId === patientId),
            procedureId,
          )
        : [],
    [active, query.data, patientId, procedureId],
  );
  return {
    packages,
    isLoading: active && query.isLoading,
    // Asked and answered. Without `package.view` nothing can be known, which a
    // caller must not read as «this patient has no package».
    isKnown: active && query.isSuccess,
    isError: active && query.isError,
  };
}

/* -------------------------------------------------------------------------- */
/* Writes                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Everything a write to a session touches.
 *
 * Recording a session moves stock through `InventoryMovement`, snapshots a unit
 * cost, may consume a package session, awards loyalty points and materialises a
 * reminder chain — all in one backend transaction. The panel cannot know which
 * of its cached lists that invalidated, so it drops the section root and the
 * lot lists together. Being slightly over-broad here costs a refetch; being
 * under-broad shows a doctor a batch that no longer has the stock they just
 * drew.
 */
function useSessionInvalidation(): () => Promise<void> {
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: sessionKeys.root(activeClinicId) }),
      // The INVENTORY section's own lists — stock on hand, valuation, expiry
      // alerts, the movement ledger — are keyed under `['inventory', clinicId]`
      // and are not reachable from the sessions root. Recording, editing,
      // cancelling or deleting a session, and adding or removing a consumption
      // line, all write `InventoryMovement` rows; without this a stock keeper
      // with both tabs open reads a quantity the clinic no longer has.
      queryClient.invalidateQueries({ queryKey: ['inventory', activeClinicId] }),
      // Patient payments and the accounting reports move with a session's price
      // and its COGS.
      queryClient.invalidateQueries({ queryKey: ['accounting'] }),
      // A session drawn from a package (or cancelled back into it) moves the
      // sale's «جلسه مونده»; the packages screen and the next form's package
      // card must not offer the session that was just used.
      queryClient.invalidateQueries({ queryKey: packageKeys.root(activeClinicId) }),
    ]);
  };
}

export function useRecordSession(): UseMutationResult<
  RecordedSessionResponse,
  unknown,
  CreateSessionDto
> {
  const invalidate = useSessionInvalidation();
  return useMutation({
    mutationFn: (dto: CreateSessionDto) => sessionsRecord(dto),
    onSuccess: invalidate,
  });
}

export function useUpdateSession(
  sessionId: string,
): UseMutationResult<SessionResponse, unknown, Parameters<typeof sessionsUpdate>[1]> {
  const invalidate = useSessionInvalidation();
  return useMutation({
    mutationFn: (dto: Parameters<typeof sessionsUpdate>[1]) => sessionsUpdate(sessionId, dto),
    onSuccess: invalidate,
  });
}

export function useCompleteSession(
  sessionId: string,
): UseMutationResult<RecordedSessionResponse, unknown, Parameters<typeof sessionsComplete>[1]> {
  const invalidate = useSessionInvalidation();
  return useMutation({
    mutationFn: (dto: Parameters<typeof sessionsComplete>[1]) => sessionsComplete(sessionId, dto),
    onSuccess: invalidate,
  });
}

export function useCancelSession(
  sessionId: string,
): UseMutationResult<SessionResponse, unknown, { reason: string; restock: boolean }> {
  const invalidate = useSessionInvalidation();
  return useMutation({
    mutationFn: (dto: { reason: string; restock: boolean }) => sessionsCancel(sessionId, dto),
    onSuccess: invalidate,
  });
}

/**
 * Soft delete. Nothing in this product hard-deletes: the row keeps a
 * `deletedAt` and the recycle bin is what empties it, after the clinic's
 * retention window, by a purge job. There is no "delete forever" button here
 * and there must never be one.
 */
export function useDeleteSession(): UseMutationResult<unknown, unknown, string> {
  const invalidate = useSessionInvalidation();
  return useMutation({
    mutationFn: (sessionId: string) => sessionsRemove(sessionId),
    onSuccess: invalidate,
  });
}

export function useAddConsumptions(
  sessionId: string,
): UseMutationResult<SessionResponse, unknown, Parameters<typeof sessionsAddConsumptions>[1]> {
  const invalidate = useSessionInvalidation();
  return useMutation({
    mutationFn: (dto: Parameters<typeof sessionsAddConsumptions>[1]) =>
      sessionsAddConsumptions(sessionId, dto),
    onSuccess: invalidate,
  });
}

/**
 * Removing a consumption line returns the stock to its lot — through a
 * compensating `InventoryMovement`, never by editing `quantityRemaining`.
 */
export function useRemoveConsumption(
  sessionId: string,
): UseMutationResult<SessionResponse, unknown, string> {
  const invalidate = useSessionInvalidation();
  return useMutation({
    mutationFn: (consumptionId: string) => sessionsRemoveConsumption(sessionId, consumptionId),
    onSuccess: invalidate,
  });
}

/**
 * «ثبت دریافت» for one session — the same ledger entry the patient file and
 * the accounting tab write (`POST /patient-payments`, `package.sell`). A
 * receipt changes the session's `balanceRials`, the patient's debt and the
 * accounting figures, so all three are dropped.
 */
export function useRecordSessionPayment(): UseMutationResult<
  unknown,
  unknown,
  CreatePatientPaymentDto
> {
  const invalidate = useSessionInvalidation();
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();
  return useMutation({
    mutationFn: (dto: CreatePatientPaymentDto) => patientPaymentsCreate(dto),
    onSuccess: async () => {
      await Promise.all([
        invalidate(),
        queryClient.invalidateQueries({ queryKey: patientKeys.all(activeClinicId) }),
      ]);
    },
  });
}

export function useSaveInjectionMap(): UseMutationResult<
  InjectionMapResponse,
  unknown,
  { mapId: string | null; create: CreateInjectionMapDto; update: UpdateInjectionMapDto }
> {
  const invalidate = useSessionInvalidation();
  return useMutation({
    mutationFn: (input: {
      mapId: string | null;
      create: CreateInjectionMapDto;
      update: UpdateInjectionMapDto;
    }) =>
      input.mapId
        ? injectionMapsUpdate(input.mapId, input.update)
        : injectionMapsCreate(input.create),
    onSuccess: invalidate,
  });
}

/**
 * «ارسال متن مراقبت» — an AFTERCARE reminder, due now.
 *
 * There is no "send this text as an SMS right now" endpoint, and inventing one
 * in the panel would put an unlogged outbound message outside the reminder
 * ledger. A reminder due at the current instant is the same message through the
 * dispatcher that records delivery, cost and failure against the session.
 */
export function useSendAftercare(): UseMutationResult<
  unknown,
  unknown,
  { patientId: string; sessionId: string }
> {
  const invalidate = useSessionInvalidation();
  return useMutation({
    mutationFn: (input: { patientId: string; sessionId: string }) =>
      remindersCreate({
        patientId: input.patientId,
        sessionId: input.sessionId,
        kind: 'AFTERCARE',
        channel: 'SMS',
        dueAt: new Date().toISOString(),
      }),
    onSuccess: invalidate,
  });
}

/**
 * «ارسال نظرسنجی رضایت» — `POST /surveys` for one completed session.
 *
 * The server keeps one open survey per session, so a second click is a 409
 * with a Persian message rather than a second SMS. It also decides whether an
 * SMS goes at all: a patient who opted out still gets a survey and a link, and
 * the answer says so in `smsSkippedReason`. The CRM section's survey lists
 * live under `['crm', clinicId]`; they are dropped so the new row is there
 * when somebody looks.
 */
export function useSendSurvey(): UseMutationResult<SurveySendResponse, unknown, string> {
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();
  return useMutation({
    mutationFn: (sessionId: string) => surveysSend({ sessionId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['crm', activeClinicId] }),
  });
}
